import { firebase_app, firebase_rdb } from '../../../Config/Config';
import { normalizePlateSearch } from '../../../utils/plateSearch';
import {
  buildPlateIndexEntry,
  normalizePlateIndexEntry,
  sortPlateIndexEntriesNewestFirst,
} from '../../../utils/ticketPlateIndex';

const CLIENT_ID = 'maxpark-220731';
const SEARCH_LIMIT = 25;
const MIN_PLATE_SEARCH_LENGTH = 3;
const MIN_EXACT_TICKET_SEARCH_LENGTH = 6;
const SEARCH_CACHE_TTL_MS = 3 * 60 * 1000;

const searchCache = new Map();
const inFlightSearches = new Map();

const forEachAgent = async (ids, visit) => {
  let next = 0;
  let failed = false;
  await Promise.all(Array.from({ length: Math.min(4, ids.length) }, async () => {
    while (!failed && next < ids.length) {
      try { await visit(ids[next++]); } catch (error) { failed = true; throw error; }
    }
  }));
};

const getClientRef = () => firebase_rdb.child('clients').child(CLIENT_ID);

const getTimeMs = () => (
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
);

const getElapsedMs = startedAt => Math.round(getTimeMs() - startedAt);

const startConsoleTimer = label => {
  if (typeof console !== 'undefined' && typeof console.time === 'function') {
    console.time(label);
  }
};

const endConsoleTimer = label => {
  if (typeof console !== 'undefined' && typeof console.timeEnd === 'function') {
    console.timeEnd(label);
  }
};

const cloneAvisMap = avisMap => new Map(avisMap || []);

const getCachedResult = cacheKey => {
  const cached = searchCache.get(cacheKey);

  if (!cached || cached.expiresAt <= Date.now()) {
    searchCache.delete(cacheKey);
    return null;
  }

  return cloneAvisMap(cached.results);
};

const setCachedResult = (cacheKey, results) => {
  searchCache.set(cacheKey, {
    expiresAt: Date.now() + SEARCH_CACHE_TTL_MS,
    results: cloneAvisMap(results),
  });
};

const createSearchMetrics = rawSearchTerm => ({
  searchTerm: rawSearchTerm,
  normalizedPlate: normalizePlateSearch(rawSearchTerm),
  searchType: 'unknown',
  cacheHit: false,
  totalDurationMs: 0,
  resultsReturned: 0,
  imageUrlReads: 0,
  ticket: {
    durationMs: 0,
    queries: 0,
    recordsRead: 0,
    detailReads: 0,
    skipped: false,
    sequentialReads: false,
    steps: [],
  },
  plate: {
    durationMs: 0,
    queries: 0,
    recordsRead: 0,
    indexEntriesRead: 0,
    detailReads: 0,
    method: 'direct ticketsByPlate path',
    directPath: '',
    legacyFallback: false,
    skipped: false,
    sequentialReads: false,
    steps: [],
  },
});

const countSnapshotRecords = snapshot => {
  if (!snapshot || !snapshot.exists()) {
    return 0;
  }

  if (typeof snapshot.numChildren === 'function') {
    return snapshot.numChildren();
  }

  let count = 0;
  snapshot.forEach(() => {
    count += 1;
    return false;
  });
  return count;
};

const recordSnapshotMetric = (metrics, group, label, snapshot, startedAt, path, options = {}) => {
  const recordsRead = countSnapshotRecords(snapshot);
  const durationMs = getElapsedMs(startedAt);

  metrics[group].queries += 1;
  metrics[group].recordsRead += recordsRead;

  if (options.indexRead) {
    metrics[group].indexEntriesRead += recordsRead;
  }

  if (options.detailRead) {
    metrics[group].detailReads += recordsRead;
  }

  metrics[group].steps.push({
    label,
    path,
    durationMs,
    recordsRead,
    readType: options.indexRead ? 'index-summary' : options.detailRead ? 'ticket-detail' : 'point',
  });

  return recordsRead;
};

const recordPointLookupMetric = (metrics, group, label, snapshot, startedAt, path) => {
  const recordsRead = snapshot && snapshot.exists() ? 1 : 0;
  const durationMs = getElapsedMs(startedAt);

  metrics[group].queries += 1;
  metrics[group].recordsRead += recordsRead;
  metrics[group].detailReads += recordsRead;
  metrics[group].steps.push({
    label,
    path,
    durationMs,
    recordsRead,
    readType: 'ticket-detail',
  });

  return recordsRead;
};

const logSearchMetrics = metrics => {
  console.info('[MaxPark avis search]', {
    searchTerm: metrics.searchTerm,
    normalizedPlate: metrics.normalizedPlate,
    searchType: metrics.searchType,
    cacheHit: metrics.cacheHit,
    totalDurationMs: metrics.totalDurationMs,
    ticketDurationMs: metrics.ticket.durationMs,
    plateDurationMs: metrics.plate.durationMs,
    ticketQueries: metrics.ticket.queries,
    plateQueries: metrics.plate.queries,
    ticketRecordsRead: metrics.ticket.recordsRead,
    plateRecordsRead: metrics.plate.recordsRead,
    plateIndexEntriesRead: metrics.plate.indexEntriesRead,
    ticketDetailReads: metrics.ticket.detailReads,
    plateFallbackDetailReads: metrics.plate.detailReads,
    imageUrlReads: metrics.imageUrlReads,
    ticketSkipped: metrics.ticket.skipped,
    plateMethod: metrics.plate.method,
    plateDirectPath: metrics.plate.directPath,
    legacyPlateFallback: metrics.plate.legacyFallback,
    plateSkipped: metrics.plate.skipped,
    sequentialTicketReads: metrics.ticket.sequentialReads,
    sequentialPlateReads: metrics.plate.sequentialReads,
    resultsReturned: metrics.resultsReturned,
    ticketSteps: metrics.ticket.steps,
    plateSteps: metrics.plate.steps,
  });
};

const getAuthenticatedUser = () => new Promise(resolve => {
  const currentUser = firebase_app.auth().currentUser;

  if (currentUser) {
    resolve(currentUser);
    return;
  }

  let resolved = false;
  const unsubscribe = firebase_app.auth().onAuthStateChanged(user => {
    if (resolved) {
      return;
    }

    resolved = true;
    unsubscribe();
    resolve(user);
  });

  setTimeout(() => {
    if (resolved) {
      return;
    }

    resolved = true;
    unsubscribe();
    resolve(firebase_app.auth().currentUser);
  }, 1500);
});

const getLegacyAgentIds = async () => {
  const agentsRef = getClientRef().child('agents');
  const currentUser = await getAuthenticatedUser();

  if (currentUser && typeof fetch === 'function') {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${agentsRef.toString()}.json?shallow=true&auth=${encodeURIComponent(token)}`);

      if (response.ok) {
        const agents = await response.json();
        return Object.keys(agents || {});
      }
    } catch (error) {
      console.warn('Unable to read shallow agent list; falling back to SDK.', error);
    }
  }

  const agentsSnapshot = await agentsRef.once('value');
  const agentIds = [];

  agentsSnapshot.forEach(agentSnapshot => {
    agentIds.push(agentSnapshot.key);
    return false;
  });

  return agentIds;
};

const normalizeSearchTerm = value => String(value || '')
  .toLowerCase()
  .replaceAll(' ', '')
  .replaceAll('/', '-');

const normalizeTicketSearchTerm = value => String(value || '').trim().replace(/\s+/g, '');

const isTicketSearchTerm = value => /^\d+$/.test(normalizeTicketSearchTerm(value));

const isExactTicketSearchTerm = value => (
  isTicketSearchTerm(value)
  && normalizeTicketSearchTerm(value).length >= MIN_EXACT_TICKET_SEARCH_LENGTH
);

const getUniqueSearchTerms = values => Array.from(new Set(
  values
    .map(value => String(value || '').trim())
    .filter(Boolean)
));

const getPlateQueryPrefixes = rawSearchTerm => {
  const trimmedSearchTerm = String(rawSearchTerm || '').trim();
  const normalizedPlateSearchTerm = normalizePlateSearch(trimmedSearchTerm);
  const broadPlatePrefix = normalizedPlateSearchTerm.slice(0, 3);

  return getUniqueSearchTerms([
    normalizedPlateSearchTerm,
    trimmedSearchTerm,
    trimmedSearchTerm.toUpperCase(),
    trimmedSearchTerm.toLowerCase(),
    broadPlatePrefix,
    broadPlatePrefix.toLowerCase(),
  ]);
};

const getExactPlateQueryPrefixes = rawSearchTerm => {
  const trimmedSearchTerm = String(rawSearchTerm || '').trim();
  const normalizedPlateSearchTerm = normalizePlateSearch(trimmedSearchTerm);

  return getUniqueSearchTerms([
    normalizedPlateSearchTerm,
    trimmedSearchTerm,
    trimmedSearchTerm.toUpperCase(),
    trimmedSearchTerm.toLowerCase(),
  ]);
};

const normalizeTicket = (ticket, fallbackId = '') => {
  const indexEntry = buildPlateIndexEntry(ticket, fallbackId);

  return {
    ...indexEntry,
    ...ticket,
    ticketid: indexEntry.ticketid,
    img: indexEntry.img,
    images: indexEntry.images,
    time: ticket?.time || '',
    date: ticket?.date || '',
    carplate: ticket?.carplate || ticket?.plate || '',
    plateNormalized: ticket?.plateNormalized || normalizePlateSearch(ticket?.carplate || ticket?.plate),
    carbrand: ticket?.carbrand || ticket?.make || '',
    carmodel: ticket?.carmodel || ticket?.model || '',
    contract: ticket?.contract || '',
    address: ticket?.address || ticket?.location || '',
  };
};

const addTicket = (avisMap, ticket, fallbackId = '') => {
  if (!ticket) {
    return;
  }

  const normalizedTicket = normalizeTicket(ticket, fallbackId);

  if (normalizedTicket.ticketid) {
    avisMap.set(normalizedTicket.ticketid, normalizedTicket);
  }
};

const addSnapshotTickets = (avisMap, snapshot, matchesSearch = () => true) => {
  snapshot.forEach(ticketSnapshot => {
    const ticket = ticketSnapshot.val();

    if (matchesSearch(ticket)) {
      addTicket(avisMap, ticket, ticketSnapshot.key);
    }

    return avisMap.size >= SEARCH_LIMIT;
  });
};

const findDirectTicket = async (avisMap, ticketId, metrics) => {
  const clientRef = getClientRef();
  const centralPath = `clients/${CLIENT_ID}/tickets/${ticketId}`;
  let startedAt = getTimeMs();
  const centralSnapshot = await clientRef.child('tickets').child(ticketId).once('value');
  recordPointLookupMetric(metrics, 'ticket', 'central ticket key', centralSnapshot, startedAt, centralPath);

  if (centralSnapshot.exists()) {
    addTicket(avisMap, centralSnapshot.val(), centralSnapshot.key);
    return;
  }

  const agentIds = metrics.agentIds || (metrics.agentIds = await getLegacyAgentIds());

  await forEachAgent(agentIds, async agentId => {
    if (avisMap.size > 0) {
      return;
    }

    const agentPath = `clients/${CLIENT_ID}/agents/${agentId}/tickets/${ticketId}`;
    const agentStartedAt = getTimeMs();
    const ticketSnapshot = await clientRef
      .child('agents')
      .child(agentId)
      .child('tickets')
      .child(ticketId)
      .once('value');
    recordPointLookupMetric(metrics, 'ticket', `legacy agent ${agentId} ticket key`, ticketSnapshot, agentStartedAt, agentPath);

    if (ticketSnapshot.exists()) {
      addTicket(avisMap, ticketSnapshot.val(), ticketSnapshot.key);
    }
  });
};

const findTicketIdPrefixMatches = async (avisMap, ticketIdPrefix, metrics) => {
  const clientRef = getClientRef();
  const endAt = `${ticketIdPrefix}\uf8ff`;
  const centralPath = `clients/${CLIENT_ID}/tickets orderByChild(ticketid) ${ticketIdPrefix}..${endAt}`;

  let startedAt = getTimeMs();
  const centralSnapshot = await clientRef
    .child('tickets')
    .orderByChild('ticketid')
    .startAt(ticketIdPrefix)
    .endAt(endAt)
    .limitToFirst(SEARCH_LIMIT)
    .once('value');
  recordSnapshotMetric(metrics, 'ticket', 'central ticketid prefix', centralSnapshot, startedAt, centralPath, { detailRead: true });

  addSnapshotTickets(avisMap, centralSnapshot);

  if (avisMap.size >= SEARCH_LIMIT) {
    return;
  }

  const agentIds = metrics.agentIds || (metrics.agentIds = await getLegacyAgentIds());

  await forEachAgent(agentIds, async agentId => {
    if (avisMap.size >= SEARCH_LIMIT) {
      return;
    }

    const agentPath = `clients/${CLIENT_ID}/agents/${agentId}/tickets orderByChild(ticketid) ${ticketIdPrefix}..${endAt}`;
    const agentStartedAt = getTimeMs();
    const ticketsSnapshot = await clientRef
      .child('agents')
      .child(agentId)
      .child('tickets')
      .orderByChild('ticketid')
      .startAt(ticketIdPrefix)
      .endAt(endAt)
      .limitToFirst(SEARCH_LIMIT)
      .once('value');
    recordSnapshotMetric(metrics, 'ticket', `legacy agent ${agentId} ticketid prefix`, ticketsSnapshot, agentStartedAt, agentPath, { detailRead: true });

    addSnapshotTickets(avisMap, ticketsSnapshot);
  });
};

const addDirectPlateIndexTickets = (avisMap, snapshot) => {
  const indexEntries = [];

  snapshot.forEach(indexEntrySnapshot => {
    indexEntries.push(normalizePlateIndexEntry(indexEntrySnapshot.val(), indexEntrySnapshot.key));
    return false;
  });

  sortPlateIndexEntriesNewestFirst(indexEntries)

    .forEach(entry => addTicket(avisMap, entry, entry.ticketid));

  return indexEntries.length;
};

// Plate results must not stop when an older index happens to contain 25 rows.
// Queries remain scoped to the entered plate; pagination belongs in the UI.
const addAllPlateTickets = (avisMap, snapshot, matchesSearch) => {
  snapshot.forEach(child => {
    if (matchesSearch(child.val())) addTicket(avisMap, child.val(), child.key);
    return false;
  });
};

const findDirectPlateIndexMatches = async (avisMap, normalizedPlateSearchTerm, metrics) => {
  const directPath = `clients/${CLIENT_ID}/indexes/ticketsByPlate/${normalizedPlateSearchTerm}`;
  metrics.plate.directPath = directPath;
  metrics.plate.method = 'direct ticketsByPlate path';

  startConsoleTimer('[plate-search] index lookup');
  const startedAt = getTimeMs();
  const indexSnapshot = await getClientRef()
    .child('indexes')
    .child('ticketsByPlate')
    .child(normalizedPlateSearchTerm)
    .once('value');
  recordSnapshotMetric(metrics, 'plate', 'direct plate summary index', indexSnapshot, startedAt, directPath, { indexRead: true });
  endConsoleTimer('[plate-search] index lookup');

  return addDirectPlateIndexTickets(avisMap, indexSnapshot);
};

const addPlateNormalizedSnapshotTickets = (avisMap, snapshot, normalizedPlateSearchTerm) => {
  addAllPlateTickets(
    avisMap,
    snapshot,
    ticket => normalizePlateSearch(ticket?.plateNormalized || ticket?.carplate) === normalizedPlateSearchTerm
  );
};

const findPlateNormalizedMatchesInTicketRef = async (
  avisMap,
  ticketsRef,
  normalizedPlateSearchTerm,
  metrics,
  label,
  path
) => {
  const startedAt = getTimeMs();
  const ticketsSnapshot = await ticketsRef
    .orderByChild('plateNormalized')
    .equalTo(normalizedPlateSearchTerm)

    .once('value');
  recordSnapshotMetric(metrics, 'plate', label, ticketsSnapshot, startedAt, path, { detailRead: true });

  addPlateNormalizedSnapshotTickets(avisMap, ticketsSnapshot, normalizedPlateSearchTerm);
};

const findPlateNormalizedMatches = async (avisMap, normalizedPlateSearchTerm, metrics, options = {}) => {
  const { includeLegacyAgents = true } = options;
  const clientRef = getClientRef();

  startConsoleTimer('[plate-search] ticket details fetch');
  await findPlateNormalizedMatchesInTicketRef(
    avisMap,
    clientRef.child('tickets'),
    normalizedPlateSearchTerm,
    metrics,
    'fallback central plateNormalized exact',
    `clients/${CLIENT_ID}/tickets orderByChild(plateNormalized) == ${normalizedPlateSearchTerm}`
  );

  if (!includeLegacyAgents) {
    endConsoleTimer('[plate-search] ticket details fetch');
    return;
  }

  const agentIds = metrics.agentIds || (metrics.agentIds = await getLegacyAgentIds());

  await forEachAgent(agentIds, async agentId => {

    await findPlateNormalizedMatchesInTicketRef(
      avisMap,
      clientRef.child('agents').child(agentId).child('tickets'),
      normalizedPlateSearchTerm,
      metrics,
      `fallback legacy agent ${agentId} plateNormalized exact`,
      `clients/${CLIENT_ID}/agents/${agentId}/tickets orderByChild(plateNormalized) == ${normalizedPlateSearchTerm}`
    );
  });
  endConsoleTimer('[plate-search] ticket details fetch');
};

const addCarPlateSnapshotTickets = (avisMap, snapshot, normalizedPlateSearchTerm) => {
  addAllPlateTickets(
    avisMap,
    snapshot,
    ticket => normalizePlateSearch(ticket?.carplate).startsWith(normalizedPlateSearchTerm)
  );
};

const findLegacyCarPlateMatchesInTicketRef = async (
  avisMap,
  ticketsRef,
  plateQueryPrefixes,
  normalizedPlateSearchTerm,
  metrics,
  label,
  basePath
) => {
  for (const platePrefix of plateQueryPrefixes) {

    const queryPath = `${basePath} orderByChild(carplate) startsWith ${platePrefix}`;
    const startedAt = getTimeMs();
    const ticketsSnapshot = await ticketsRef
      .orderByChild('carplate')
      .startAt(platePrefix)
      .endAt(`${platePrefix}\uf8ff`)
      .once('value');
    recordSnapshotMetric(metrics, 'plate', `${label} legacy carplate prefix ${platePrefix}`, ticketsSnapshot, startedAt, queryPath, { detailRead: true });

    addCarPlateSnapshotTickets(avisMap, ticketsSnapshot, normalizedPlateSearchTerm);
  }
};

const findLegacyCarPlatePrefixMatches = async (
  avisMap,
  rawSearchTerm,
  normalizedPlateSearchTerm,
  metrics,
  options = {}
) => {
  const {
    includeLegacyAgents = true,
    plateQueryPrefixes = getPlateQueryPrefixes(rawSearchTerm),
  } = options;
  const clientRef = getClientRef();

  metrics.plate.legacyFallback = true;
  metrics.plate.method = includeLegacyAgents
    ? 'direct ticketsByPlate path with legacy ticket-query fallback'
    : 'direct ticketsByPlate path with central ticket-query fallback';

  await findLegacyCarPlateMatchesInTicketRef(
    avisMap,
    clientRef.child('tickets'),
    plateQueryPrefixes,
    normalizedPlateSearchTerm,
    metrics,
    'fallback central',
    `clients/${CLIENT_ID}/tickets`
  );

  if (!includeLegacyAgents) {
    return;
  }

  const agentIds = metrics.agentIds || (metrics.agentIds = await getLegacyAgentIds());

  await forEachAgent(agentIds, async agentId => {

    await findLegacyCarPlateMatchesInTicketRef(
      avisMap,
      clientRef.child('agents').child(agentId).child('tickets'),
      plateQueryPrefixes,
      normalizedPlateSearchTerm,
      metrics,
      `fallback legacy agent ${agentId}`,
      `clients/${CLIENT_ID}/agents/${agentId}/tickets`
    );
  });
};

const findCarPlateMatches = async (avisMap, rawSearchTerm, metrics, options = {}) => {
  const { deferLegacyPlateFallback = false } = options;
  startConsoleTimer('[plate-search] normalize');
  const normalizedPlateSearchTerm = normalizePlateSearch(rawSearchTerm);
  endConsoleTimer('[plate-search] normalize');

  const startingSize = avisMap.size;

  if (
    !normalizedPlateSearchTerm
    || normalizedPlateSearchTerm.length < MIN_PLATE_SEARCH_LENGTH
  ) {
    metrics.plate.skipped = normalizedPlateSearchTerm.length < MIN_PLATE_SEARCH_LENGTH
      ? 'plate search term shorter than 3 characters'
      : false;
    return;
  }

  const indexEntriesRead = await findDirectPlateIndexMatches(avisMap, normalizedPlateSearchTerm, metrics);

  metrics.plate.legacyFallback = true;
  metrics.plate.method = indexEntriesRead > 0 || avisMap.size > startingSize
    ? 'direct ticketsByPlate path with ticket-record fallback'
    : metrics.plate.method;

  await findPlateNormalizedMatches(avisMap, normalizedPlateSearchTerm, metrics, {
    includeLegacyAgents: !deferLegacyPlateFallback,
  });

  if (deferLegacyPlateFallback) {
    await findLegacyCarPlatePrefixMatches(avisMap, rawSearchTerm, normalizedPlateSearchTerm, metrics, {
      includeLegacyAgents: false,
      plateQueryPrefixes: getExactPlateQueryPrefixes(rawSearchTerm),
    });
    return;
  }

  await findLegacyCarPlatePrefixMatches(avisMap, rawSearchTerm, normalizedPlateSearchTerm, metrics);
  // Canonical copies win over historical mirrors (including their photo/status fields).
  await findPlateNormalizedMatches(avisMap, normalizedPlateSearchTerm, metrics, { includeLegacyAgents: false });
  await findLegacyCarPlatePrefixMatches(avisMap, rawSearchTerm, normalizedPlateSearchTerm, metrics, {
    includeLegacyAgents: false,
    plateQueryPrefixes: getExactPlateQueryPrefixes(rawSearchTerm),
  });
};

const runSearch = async (rawSearchTerm, options = {}) => {
  const {
    directTicketOnly = false,
    skipPlateForTicket = false,
  } = options;
  const normalizedSearchTerm = normalizeSearchTerm(rawSearchTerm);
  const normalizedTicketSearchTerm = normalizeTicketSearchTerm(rawSearchTerm);
  const avisMap = new Map();
  const metrics = createSearchMetrics(rawSearchTerm);
  const totalStartedAt = getTimeMs();

  if (!normalizedSearchTerm) {
    return avisMap;
  }

  try {
    startConsoleTimer('[plate-search] total');
    let stageStartedAt = getTimeMs();

    if (isTicketSearchTerm(rawSearchTerm)) {
      metrics.searchType = 'ticket';
      await findDirectTicket(avisMap, normalizedTicketSearchTerm, metrics);

      if (!directTicketOnly && avisMap.size < SEARCH_LIMIT) {
        await findTicketIdPrefixMatches(avisMap, normalizedTicketSearchTerm, metrics);
      } else if (directTicketOnly) {
        metrics.ticket.skipped = 'ticket prefix fallback deferred by fast exact-ticket search';
      }
      metrics.ticket.durationMs = getElapsedMs(stageStartedAt);
    } else {
      metrics.searchType = 'plate';
      metrics.ticket.skipped = 'non-numeric search term';
    }

    if (avisMap.size < SEARCH_LIMIT) {
      stageStartedAt = getTimeMs();
      if (skipPlateForTicket && isTicketSearchTerm(rawSearchTerm)) {
        metrics.plate.skipped = 'plate fallback deferred by fast exact-ticket search';
      } else {
        await findCarPlateMatches(avisMap, rawSearchTerm, metrics, options);
      }
      metrics.plate.durationMs = getElapsedMs(stageStartedAt);
    }

    startConsoleTimer('[plate-search] image url fetch');
    endConsoleTimer('[plate-search] image url fetch');

    metrics.totalDurationMs = getElapsedMs(totalStartedAt);
    metrics.resultsReturned = avisMap.size;
    logSearchMetrics(metrics);
    endConsoleTimer('[plate-search] total');

    return avisMap;
  } catch (error) {
    metrics.totalDurationMs = getElapsedMs(totalStartedAt);
    metrics.resultsReturned = avisMap.size;
    metrics.error = error.message;
    logSearchMetrics(metrics);
    endConsoleTimer('[plate-search] total');
    console.error('An error occurred while searching avis', error);
    throw error;
  }
};

const areSearchResultsEqual = (left, right) => {
  if (!left || !right || left.size !== right.size) {
    return false;
  }

  for (const key of left.keys()) {
    if (!right.has(key)) {
      return false;
    }
  }

  return true;
};

const runCachedFullSearch = async (cacheKey, rawSearchTerm) => {
  if (inFlightSearches.has(cacheKey)) {
    return cloneAvisMap(await inFlightSearches.get(cacheKey));
  }

  const searchPromise = runSearch(rawSearchTerm).then(results => {
    setCachedResult(cacheKey, results);
    return results;
  });

  inFlightSearches.set(cacheKey, searchPromise);

  try {
    return cloneAvisMap(await searchPromise);
  } finally {
    inFlightSearches.delete(cacheKey);
  }
};

const runCachedFastExactTicketSearch = async (cacheKey, rawSearchTerm) => {
  if (inFlightSearches.has(cacheKey)) {
    return cloneAvisMap(await inFlightSearches.get(cacheKey));
  }

  const searchPromise = runSearch(rawSearchTerm, {
    directTicketOnly: true,
    skipPlateForTicket: true,
  }).then(results => {
    setCachedResult(cacheKey, results);
    return results;
  });

  inFlightSearches.set(cacheKey, searchPromise);

  try {
    return cloneAvisMap(await searchPromise);
  } finally {
    inFlightSearches.delete(cacheKey);
  }
};

export const fetchData = async (pMatchWord, options = {}) => {
  const { onUpdate, onError, preferFast = false } = options || {};
  const rawSearchTerm = String(pMatchWord || '').trim();
  const cacheKey = `${CLIENT_ID}:${isTicketSearchTerm(rawSearchTerm) ? 'ticket' : 'plate'}:${isTicketSearchTerm(rawSearchTerm) ? normalizeTicketSearchTerm(rawSearchTerm) : normalizePlateSearch(rawSearchTerm) || normalizeSearchTerm(rawSearchTerm)}`;
  const cachedResult = options.skipCache ? null : getCachedResult(cacheKey);

  if (cachedResult) {
    console.info('[MaxPark avis search]', {
      searchTerm: rawSearchTerm,
      normalizedPlate: normalizePlateSearch(rawSearchTerm),
      cacheHit: true,
      cacheKey,
      resultsReturned: cachedResult.size,
    });
    return cachedResult;
  }

  if (preferFast && isExactTicketSearchTerm(rawSearchTerm)) {
    const fastCacheKey = `${cacheKey}:fast-exact`;
    const fastResult = await runCachedFastExactTicketSearch(fastCacheKey, rawSearchTerm);

    if (!fastResult.size && typeof onUpdate === 'function') {
      runCachedFullSearch(cacheKey, rawSearchTerm).then(fullResult => {
        if (!areSearchResultsEqual(fastResult, fullResult)) {
          onUpdate(cloneAvisMap(fullResult));
        }
      }).catch(error => { if (typeof onError === 'function') onError(error); });
    }

    return fastResult;
  }

  return runCachedFullSearch(cacheKey, rawSearchTerm);
};
